import { useCallback, useEffect, useState } from "react"

export type ThemeMode = "dark" | "light" | "system"

const THEME_MODE_KEY = "theme"

function systemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

// Anything that is not an explicit dark/light choice (absent key, legacy or
// unknown values) counts as system mode.
function readStoredMode(): ThemeMode {
  const stored = localStorage.getItem(THEME_MODE_KEY)
  if (stored === "dark" || stored === "light") {
    return stored
  }
  return "system"
}

export function useDarkMode() {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(readStoredMode)
  const [systemIsDark, setSystemIsDark] = useState(systemPrefersDark)

  const setThemeMode = useCallback((mode: ThemeMode)=> {
    localStorage.setItem(THEME_MODE_KEY, mode)
    setThemeModeState(mode)
  }, [])

  // Keep the resolved value in sync while in system mode.
  useEffect(()=> {
    const mq = window.matchMedia("(prefers-color-scheme: dark)")
    const onChange = ()=> setSystemIsDark(mq.matches)
    mq.addEventListener("change", onChange)
    return ()=> mq.removeEventListener("change", onChange)
  }, [])

  const isDarkMode =
    themeMode === "dark" || (themeMode === "system" && systemIsDark)

  useEffect(()=> {
    document.documentElement.classList.toggle("dark", isDarkMode)
  }, [isDarkMode])

  return { isDarkMode, setThemeMode, themeMode }
}